import React, { useState, useEffect } from 'react';
import { useMentorAuth } from '../contexts/MentorAuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Calendar, Clock, Edit, CheckCircle, XCircle, PlayCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MentorSessions() {
  const { getToken } = useMentorAuth();
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState('all'); // all, upcoming, past
  const [isLoading, setIsLoading] = useState(true);
  const [editingSession, setEditingSession] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    duration: 90,
    meeting_url: '',
    description: '',
    summary: '',
    status: 'upcoming'
  });

  useEffect(() => {
    fetchSessions();
  }, [filter]);

  const fetchSessions = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      let endpoint = '/api/mentor/sessions';
      
      if (filter === 'upcoming') {
        endpoint = '/api/mentor/sessions/upcoming';
      } else if (filter === 'past') {
        endpoint = '/api/mentor/sessions/past';
      }
      
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSessions(response.data.sessions);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar sesiones');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    setFormData({
      title: session.title,
      date: session.date.slice(0, 16),
      duration: session.duration,
      meeting_url: session.meeting_url || '',
      description: session.description || '',
      summary: session.summary || '',
      status: session.status
    });
  };

  const handleSave = async () => {
    try {
      const token = getToken();
      await axios.patch(
        `${BACKEND_URL}/api/mentor/sessions/${editingSession.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Sesión actualizada exitosamente');
      setEditingSession(null);
      fetchSessions();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar sesión');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: PlayCircle, text: 'En Vivo' };
      case 'upcoming':
        return { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Calendar, text: 'Próxima' };
      case 'replay':
        return { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, text: 'Completada' };
      default:
        return { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: XCircle, text: status };
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Mis Sesiones</h1>
          <p className="text-gray-400">Gestiona todas tus sesiones asignadas</p>
        </div>

        {/* Filters */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'all'
                ? 'bg-teal-500 text-white'
                : 'bg-black/30 text-gray-400 hover:bg-black/50'
            }`}
          >
            Todas ({sessions.length})
          </button>
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'upcoming'
                ? 'bg-blue-500 text-white'
                : 'bg-black/30 text-gray-400 hover:bg-black/50'
            }`}
          >
            Próximas
          </button>
          <button
            onClick={() => setFilter('past')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'past'
                ? 'bg-green-500 text-white'
                : 'bg-black/30 text-gray-400 hover:bg-black/50'
            }`}
          >
            Pasadas
          </button>
        </div>

        {/* Sessions List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-white">Cargando...</div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-12 text-center">
            <Calendar className="mx-auto text-gray-600 mb-4" size={48} />
            <p className="text-gray-400">No hay sesiones para mostrar</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => {
              const badge = getStatusBadge(session.status);
              const BadgeIcon = badge.icon;
              
              return (
                <div
                  key={session.id}
                  className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-teal-500/30 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-white">{session.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${badge.color}`}>
                          <BadgeIcon size={14} />
                          {badge.text}
                        </span>
                      </div>
                      <p className="text-gray-400 mb-3">{session.pilar}</p>
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <span className="flex items-center gap-2">
                          <Calendar size={16} />
                          {new Date(session.date).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="flex items-center gap-2">
                          <Clock size={16} />
                          {session.duration} minutos
                        </span>
                      </div>
                      {session.description && (
                        <p className="mt-3 text-sm text-gray-400">{session.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleEdit(session)}
                      className="flex items-center space-x-2 px-4 py-2 bg-teal-500/10 text-teal-400 rounded-lg hover:bg-teal-500/20 transition-colors"
                    >
                      <Edit size={16} />
                      <span>Editar</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Edit Modal */}
        {editingSession && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90">
            <div className="bg-black/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-white mb-6">Editar Sesión</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Título</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Fecha y Hora</label>
                  <input
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Duración (minutos)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">URL de Reunión</label>
                  <input
                    type="url"
                    value={formData.meeting_url}
                    onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
                    className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Descripción</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Resumen (para estudiantes)</label>
                  <textarea
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    rows="2"
                    className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  >
                    <option value="upcoming">Próxima</option>
                    <option value="live">En Vivo</option>
                    <option value="replay">Completada</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditingSession(null)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
